import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { Slider } from '@/components/ui/slider';
import { formatDuration } from '@/lib/format-duration';

import { PodcastActions } from './PodcastActions';

interface AudioPlayerProps {
  src: string;
  durationHintSec?: number | undefined;
  showDownload?: boolean;
  onDownload?: (() => void) | undefined;
}

export function AudioPlayer({
  src,
  durationHintSec,
  showDownload = false,
  onDownload,
}: AudioPlayerProps): ReactElement {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState<number>(durationHintSec ?? 0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setIsPlaying(false);
    setCurrentSec(0);
  }, [src]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play();
    } else {
      a.pause();
    }
  }, []);

  const handleSeek = useCallback((next: number[]) => {
    const a = audioRef.current;
    const target = next[0];
    if (!a || target === undefined) return;
    a.currentTime = target;
    setCurrentSec(target);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <PodcastActions
        isPlaying={isPlaying}
        onTogglePlay={togglePlay}
        {...(showDownload && onDownload ? { onDownload } : {})}
      />

      <Slider
        value={[currentSec]}
        max={Math.max(0.1, durationSec)}
        step={0.1}
        onValueChange={handleSeek}
        aria-label="Posição do áudio"
        className="w-full"
      />

      <div className="text-muted-foreground flex justify-between text-xs tabular-nums">
        <span>{formatDuration(currentSec, 'seconds')}</span>
        <span>{formatDuration(durationSec, 'seconds')}</span>
      </div>

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const a = e.currentTarget;
          if (Number.isFinite(a.duration) && a.duration > 0) {
            setDurationSec(a.duration);
          }
        }}
        onTimeUpdate={(e) => {
          setCurrentSec(e.currentTarget.currentTime);
        }}
        onPlay={() => {
          setIsPlaying(true);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentSec(0);
        }}
        className="hidden"
      />
    </div>
  );
}
